import { Card } from "@bp/ui-components";
import { useState } from "react";
import {
  type LicensedAppEdge,
  LicensedProductList,
} from "../../LicensedProductList/LicensedProductList";
import { LicenseModal } from "../../Organizations/modals/LicenseModal";
import { isUserFacingClient } from "../../../utils/product";
import { StatusBadge } from "../../StatusBadge/StatusBadge";

type Props = {
  org?: { uuid: string; name: string } | null;
  /** The org's licensed-application edges (fetched by the page). */
  edges: readonly LicensedAppEdge[];
  /** Active profile and the application uuids it can reach (direct or org-wide). */
  profile?: { uuid: string; name: string; grantedAppIds: readonly string[] } | null;
  onChanged: () => void;
};

/**
 * One card for "which products does this profile have": every product the
 * organization has licensed, with whether the active profile can reach it.
 * Editing opens the same licence dialog as the profile menu, so the diagnosis,
 * this card and the menu never disagree.
 */
export function ProductAccessList({ org, edges, profile, onChanged }: Props) {
  const [licensing, setLicensing] = useState(false);
  const granted = new Set(profile?.grantedAppIds ?? []);
  // API clients are an organization matter; a person never signs in to them.
  const products = edges.filter((e) => isUserFacingClient(e.node?.clientId ?? ""));
  const hasAccess = (e: LicensedAppEdge) =>
    (e.mergedNodeUuids ?? [e.node?.uuid]).some((uuid) => uuid && granted.has(uuid));
  const canEdit = Boolean(org && profile && products.length > 0);

  return (
    <Card
      header={{
        headline: "Produkt-Zugriff",
        actions: canEdit
          ? [
              {
                text: "Zugriff bearbeiten",
                hierarchy: "secondary",
                callback: () => setLicensing(true),
              },
            ]
          : undefined,
      }}
    >
      {org && profile ? (
        <>
          <div
            style={{
              fontSize: "var(--font-size-s)",
              color: "var(--color-grey)",
              marginBottom: "var(--spacing-2)",
            }}
          >
            Von {org.name} gebuchte Produkte und ob dieses Profil sie nutzen kann.
          </div>
          <LicensedProductList
            edges={products}
            renderStatus={(e) =>
              hasAccess(e) ? (
                <StatusBadge status="ok" label="Zugriff" />
              ) : (
                <StatusBadge status="neutral" label="Kein Zugriff" />
              )
            }
          />
        </>
      ) : (
        <p style={{ color: "var(--color-grey)", margin: 0 }}>Kein Profil-Kontext gewählt.</p>
      )}
      {licensing && org && profile && (
        <LicenseModal
          org={org}
          profile={{ uuid: profile.uuid, name: profile.name }}
          onClose={() => setLicensing(false)}
          onSaved={() => {
            setLicensing(false);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}
